import { useIntl, defineMessages } from 'react-intl';
import { graphql, useStaticQuery } from 'gatsby';
import { useConfiguration } from '@printdeal/configuration/react';
import { Message } from '../types/Message';

const messages: { [key: string]: Message } = defineMessages({
  slogan: {
    id: 'company.slogan',
    defaultMessage: 'Show who you are',
  },
  description: {
    id: 'company.description',
    defaultMessage: 'Ordering printed matter online has never been easier. Show who you are with your own flyers, letterheads, business cards, banners and other printed communications. You can order fast and cheap printing at',
  },
  productSupported: {
    id: 'company.contactPoint.productSupported',
    defaultMessage: 'Online Printing Company',
  },
});

interface QueryData {
  kiyohReview: {
    averageRating: number;
    numberReviews: number;
  };
  homepage: {
    nodes: {
      node_locale: string;
      path: string;
      seoDescription: {
        seoDescription: string;
      };
    }[];
  };
}

const OrganizationStructuredData = () => {
  const { kiyohReview, homepage } = useStaticQuery<QueryData>(
    graphql`
        {
          kiyohReview: kiyoh {
            averageRating
            numberReviews
          }
          homepage: allContentfulPageSystem(filter: { pageSytemType: { eq: "home" } }) {
            nodes {
              node_locale
              path
              seoDescription {
                seoDescription
              }
            }
          }
        }
    `,
  );
  const {
    channel,
    organisationName,
    logo,
    officeAddress,
    contactPoint,
    priceRange,
    paymentAccepted,
    openingHours: openingHoursSpecification,
    social,
  } = useConfiguration();
  interface Node {
    node_locale: string;
    seoDescription: {
      seoDescription: string;
    };
  }
  const intl = useIntl();
  const homepageForLocale = homepage.nodes.find((node: Node) => node.node_locale === intl.locale);

  const url = `${channel.siteUrl}${homepageForLocale?.path}`;

  const slogan = intl.formatMessage({
    id: messages.slogan.id,
    defaultMessage: messages.slogan.defaultMessage,
  });
  const description = intl.formatMessage({
    id: messages.description.id,
    defaultMessage: messages.description.defaultMessage,
  });
  const productSupported = intl.formatMessage({
    id: messages.productSupported.id,
    defaultMessage: messages.productSupported.defaultMessage,
  });

  const organizationData = {
    '@context': 'https://schema.org/',
    '@graph': [{
      '@type': 'Organization',
      '@id': `${channel.siteUrl}/#organization`,
      name: organisationName,
      url,
      logo: logo.companyLogo,
      image: logo.companyLogo,
      slogan,
      description,
      sameAs: [
        social.facebook.uri,
        social.instagram.uri,
        social.twitter.uri,
        social.linkedin.uri,
        social.pinterest.uri,
      ],
      brand: {
        '@type': 'Brand',
        name: organisationName,
      },
      telephone: officeAddress.phoneNumber,
      email: contactPoint.email,
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: officeAddress.phoneNumber,
        email: contactPoint.email,
        contactType: 'customer service',
        areaServed: contactPoint.areaServed,
        availableLanguage: contactPoint.availableLanguage,
        productSupported,
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${officeAddress.streetName} ${officeAddress.streetNumber}`,
        addressLocality: officeAddress.city,
        postalCode: officeAddress.postalCode,
        addressCountry: officeAddress.countryCode,
      },
    }, {
      '@type': 'LocalBusiness',
      '@id': `${channel.siteUrl}/#localbusiness`,
      name: organisationName,
      url,
      image: logo.companyLogo,
      telephone: officeAddress.phoneNumber,
      email: contactPoint.email,
      priceRange,
      paymentAccepted,
      address: {
        '@type': 'PostalAddress',
        streetAddress: `${officeAddress.streetName} ${officeAddress.streetNumber}`,
        addressLocality: officeAddress.city,
        postalCode: officeAddress.postalCode,
        addressCountry: officeAddress.countryCode,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: contactPoint.geoCoordinates.latitude,
        longitude: contactPoint.geoCoordinates.longitude,
      },
      openingHoursSpecification: openingHoursSpecification.map((openingHours) => ({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: openingHours.dayOfWeek,
        opens: openingHours.opens,
        closes: openingHours.closes,
      })),
      aggregateRating: {
        '@type': 'AggregateRating',
        bestRating: 10,
        worstRating: 1,
        ratingValue: kiyohReview.averageRating,
        reviewCount: kiyohReview.numberReviews,
      },
      parentOrganization: {
        '@id': `${channel.siteUrl}/#organization`,
      },
    }],
  };

  // https://schema.org/Organization
  return (
    <script
      type="application/ld+json"
      id="organization-structured-data"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationData) }}
    />
  );
};

export default OrganizationStructuredData;
